import {Directive, EventEmitter, Input, Output} from '@angular/core';
import {RowSortValueAccessor, sortLoadedRows} from '../../util/row-sort';

export type SortDirection = 'ASC' | 'DESC';

export type AriaSort = 'ascending' | 'descending' | 'none' | null;

export interface TableSortChange
{
    field: string;
    order: SortDirection;
}

export interface SortDropdownOption
{
    id: string;
    name: string;
}

/**
 * Sorting shared by the result tables. A subclass supplies its rows, its sortable columns, which of them may be
 * sorted, and how each one reads its value.
 *
 * <p>
 * A sort click either emits {@link #sortChange} for the parent to re-query — what the paged list pages need, since
 * there the server decides which rows land on the page — or, with {@link #clientSideSort}, reorders the rows already
 * held, for fixed-size dashboard cards where re-querying would change which rows the card contains.
 *
 * <p>
 * Sortability is decided here rather than in the subclass: {@link #isColumnSortable} folds the optional
 * {@link #sortableColumns} whitelist over {@link #isSortableField}, so a new table cannot accidentally ignore the
 * whitelist. Passing an empty whitelist disables sorting outright — the headers go inert and the "Sort By" toolbar is
 * not rendered. A subclass only declares its {@link #sortableColumnOptions}; {@link #isSortableField} defaults to
 * those, and exists as an override for the rare field that should be sortable without appearing in the dropdown.
 *
 * <p>
 * Sortability alone does not make a column sortable <em>here</em>: that needs a {@link #sortValues} accessor too. A
 * client-side column declared without one reads as unsorted rather than sorting silently wrong — see
 * {@link #activeSortField}. The per-table specs assert the two lists agree, so the miss surfaces as a failing test
 * rather than as headers claiming an order the rows do not have.
 */
@Directive()
export abstract class SortableTableBase<T>
{
    @Input() clientSideSort = false;

    @Input() sortField: string | null = null;

    @Input() sortOrder: SortDirection = 'DESC';

    @Output() readonly sortChange = new EventEmitter<TableSortChange>();

    private columnWhitelist: ReadonlySet<string> | null = null;

    private cachedDropdownOptions: SortDropdownOption[] | null = null;

    private cachedSortableFieldIds: ReadonlySet<string> | null = null;

    private clientSortField: string | null = null;

    private clientSortOrder: SortDirection | null = null;

    private sortedRows: T[] = [];

    private readonly noRows: T[] = [];

    private sortedFrom: ReadonlyArray<T> | null = null;

    private sortedByField: string | null = null;

    private sortedByOrder: SortDirection | null = null;

    protected abstract get rows(): T[] | null | undefined;

    protected abstract get sortableColumnOptions(): ReadonlyArray<SortDropdownOption>;

    protected abstract get sortValues(): Record<string, RowSortValueAccessor<T>>;

    protected isSortableField(field: string): boolean
    {
        this.cachedSortableFieldIds ??= new Set(this.sortableColumnOptions.map(column => column.id));
        return this.cachedSortableFieldIds.has(field);
    }

    @Input()
    set sortableColumns(sortableColumns: ReadonlySet<string> | null)
    {
        this.columnWhitelist = sortableColumns;
        this.cachedDropdownOptions = null;
    }

    get sortableColumns(): ReadonlySet<string> | null
    {
        return this.columnWhitelist;
    }

    get activeSortField(): string | null
    {
        const field = this.clientSortField ?? this.sortField;
        if (field === null || (this.clientSideSort && !this.sortValues[field]))
        {
            return null;
        }
        return field;
    }

    get activeSortOrder(): SortDirection
    {
        return this.clientSortOrder ?? this.sortOrder;
    }

    get displayedRows(): T[]
    {
        const source = this.rows ?? this.noRows;
        if (!this.clientSideSort)
        {
            return source;
        }

        const field = this.activeSortField;
        const order = this.activeSortOrder;
        if (source !== this.sortedFrom || field !== this.sortedByField || order !== this.sortedByOrder)
        {
            this.sortedRows = sortLoadedRows(source, field === null ? null : this.sortValues[field], order);
            this.sortedFrom = source;
            this.sortedByField = field;
            this.sortedByOrder = order;
        }
        return this.sortedRows;
    }

    get sortDropdownOptions(): SortDropdownOption[]
    {
        this.cachedDropdownOptions ??= this.sortableColumnOptions.filter(column => this.isColumnSortable(column.id));
        return this.cachedDropdownOptions;
    }

    isColumnSortable(field: string): boolean
    {
        if (!this.isSortableField(field))
        {
            return false;
        }
        return this.columnWhitelist === null || this.columnWhitelist.has(field);
    }

    onSort(field: string): void
    {
        if (!this.isColumnSortable(field))
        {
            return;
        }
        this.applySort(field, this.nextOrderFor(field));
    }

    onSortFieldChange(field: string | null | undefined): void
    {
        if (!field || !this.isColumnSortable(field))
        {
            return;
        }
        this.applySort(field, 'DESC');
    }

    onSortDirectionToggle(): void
    {
        const field = this.activeSortField;
        if (!field || !this.isColumnSortable(field))
        {
            return;
        }
        this.applySort(field, this.activeSortOrder === 'ASC' ? 'DESC' : 'ASC');
    }

    sortClass(field: string): string
    {
        if (this.activeSortField !== field)
        {
            return 'fa fa-sort';
        }
        return this.activeSortOrder === 'DESC' ? 'fa fa-sort-desc' : 'fa fa-sort-asc';
    }

    ariaSort(field: string): AriaSort
    {
        if (!this.isColumnSortable(field))
        {
            return null;
        }
        if (this.activeSortField !== field)
        {
            return 'none';
        }
        return this.activeSortOrder === 'ASC' ? 'ascending' : 'descending';
    }

    trackRowKey(id: number | string | null | undefined, index: number): string
    {
        return id === null || id === undefined ? `index:${index}` : `id:${id}`;
    }

    /**
     * Flips direction on the already-active column, otherwise starts a newly-selected column descending.
     *
     * @param field the column being clicked
     * @return the resulting sort direction
     */
    private nextOrderFor(field: string): SortDirection
    {
        if (this.activeSortField !== field)
        {
            return 'DESC';
        }
        return this.activeSortOrder === 'ASC' ? 'DESC' : 'ASC';
    }

    /**
     * Reorders here or hands the sort upward — never both, so a client-side table can't trigger a parent re-query.
     *
     * @param field the column to sort on
     * @param order the direction to apply
     */
    private applySort(field: string, order: SortDirection): void
    {
        if (!this.clientSideSort)
        {
            this.sortChange.emit({field, order});
            return;
        }
        this.clientSortField = field;
        this.clientSortOrder = order;
    }
}
